// Joana chat shell — what the conversation is drawn ON (G16).
//
// ## The TypeScript twin of `ChatWallpaperCatalog`
//
// Every id, label and colour here is transcribed from the shared module
// (`mobile/shared/.../domain/Appearance.kt`), the way `appearance.ts` repeats
// `Appearance.ThemeChoice` and `i18n/index.ts` repeats `InterfaceLanguage`.
// The storage keys and the encoding are IDENTICAL on purpose: a person who
// picks Sand on their phone and reads about it on the web should find the same
// seven names meaning the same seven things.
//
// **A hand-copied table rots, and its neighbours hide it.** So this one is
// COMPARED against the Kotlin, value for value, by
// `scripts/check-wallpaper-mirror.mjs`, which runs in the `web` gate. That is
// also why the raw hexes below are allowed past the token lint: the lint asks
// "does this file state a colour the appearance cannot reach", and every entry
// here states BOTH appearances, which is the thing the lint is protecting.
//
// ## Stored per BROWSER, like the appearance
//
// Same reason, and it is the shared module's, not an oversight: what a screen
// should look like is a property of that screen. See `appearance.ts`.
//
// ## The web draws the canvas and NOT the doodle
//
// The phones stroke a brand doodle over the canvas at a low alpha, and the
// dark variants below are deliberately close in luminance because on a phone
// the doodle is what carries the choice. Joana's catalog has no primitive that
// draws it — and a hand-rolled substitute would be a different pattern from
// the phones', which is worse than none. So the dark options here read as
// tints of near-black, which is honest but quieter than the phones. The doodle
// is owed; `patternLight`/`patternDark` are carried anyway so the day it lands
// nothing has to be re-transcribed.

/**
 * One wallpaper: a canvas fill and a doodle tint, stated for BOTH appearances.
 *
 * The values are written in the shared module's own packing — **`#AARRGGBB`,
 * alpha FIRST** — because this table is compared against the Kotlin literal
 * for literal and a re-ordered copy could not be. CSS reads the opposite
 * (`#RRGGBBAA`), so nothing may hand one of these to a stylesheet without
 * `cssColor` first. Found by driving it: `#FFF3E9D9` reached the page intact
 * and Chrome computed `rgba(255, 243, 233, 0.85)` — the right hue at 85%
 * opacity over whatever was behind it, which is not a colour anybody chose.
 */
export interface ChatWallpaper {
  /** Stable and storable; never shown. */
  id: string;
  /** What the picker calls it. Passed through `t()` at the draw. */
  label: string;
  canvasLight: string;
  patternLight: string;
  canvasDark: string;
  patternDark: string;
}

/**
 * The catalogue, in picker order — `default` first, because it is what a
 * person is already looking at when they open the screen.
 *
 * Adding one is not a free choice: the bubbles do not change with the
 * wallpaper, so every light variant must stay light enough for near-ink text
 * on a white bubble and every dark variant dark enough for the reverse. That
 * decision lives in the shared module; this table follows it.
 */
export const WALLPAPERS: ChatWallpaper[] = [
  { id: "default", label: "WaveTalk", canvasLight: "#FFF6EEE6", patternLight: "#0E171921", canvasDark: "#FF14111A", patternDark: "#0DF3F1F6" },
  { id: "plain", label: "Plain", canvasLight: "#FFEDECF1", patternLight: "#0A171921", canvasDark: "#FF000000", patternDark: "#12F3F1F6" },
  { id: "sand", label: "Sand", canvasLight: "#FFF3E9D9", patternLight: "#12171921", canvasDark: "#FF191510", patternDark: "#0FF6EBD8" },
  { id: "teal", label: "Teal", canvasLight: "#FFE6F0EC", patternLight: "#0E13211C", canvasDark: "#FF101A17", patternDark: "#0FDFF3EC" },
  { id: "indigo", label: "Indigo", canvasLight: "#FFE9EAF4", patternLight: "#0E15172A", canvasDark: "#FF12131F", patternDark: "#0FE4E6F7" },
  { id: "rose", label: "Rose", canvasLight: "#FFF7E9EC", patternLight: "#0E24141A", canvasDark: "#FF1A1014", patternDark: "#0FF7DDE4" },
  { id: "slate", label: "Slate", canvasLight: "#FFECEEF1", patternLight: "#0E171921", canvasDark: "#FF15171B", patternDark: "#0FEDF0F4" },
];

/**
 * `#AARRGGBB` (the shared module's packing) → `#RRGGBBAA` (what CSS reads).
 *
 * The one place the two orderings meet. Every value in this file is in the
 * Kotlin's order so the mirror gate can compare them as text, and every value
 * that reaches a stylesheet goes through here.
 *
 * A fully opaque colour comes back as six digits rather than eight, because
 * `#RRGGBBFF` and `#RRGGBB` are the same colour and the shorter one is what a
 * person reading the page's styles expects to see.
 */
export function cssColor(argb: string): string {
  const h = argb.replace(/^#/, "");
  if (h.length !== 8) return argb;
  const a = h.slice(0, 2);
  const rgb = h.slice(2);
  return a.toUpperCase() === "FF" ? `#${rgb}` : `#${rgb}${a}`;
}

/** Where the choice lives. The same key the phones use. */
export const WALLPAPER_KEY = "appearance.wallpaper";

export const DEFAULT_WALLPAPER: ChatWallpaper = WALLPAPERS[0];

/** The wallpaper with this id, or null if this build does not ship it. */
export function wallpaperById(id: string | null | undefined): ChatWallpaper | null {
  return WALLPAPERS.find((w) => w.id === id) ?? null;
}

/** Read a stored choice. Anything unrecognised — an older build's value, a
 *  truncated write, nothing at all — reads as the default, because failing to
 *  a working canvas beats failing to none. */
export function decodeWallpaper(stored: string | null | undefined): ChatWallpaper {
  return wallpaperById(stored) ?? DEFAULT_WALLPAPER;
}

/**
 * The CSS the host writes so the canvas follows the appearance WITHOUT a
 * re-render.
 *
 * This is the shape of the whole feature. A hex chosen in the tree would be
 * the light colour forever: the appearance switches in CSS — `data-appearance`
 * on the root, or `prefers-color-scheme` when nobody has chosen — and no
 * render pass runs when the machine's lights change. So the wallpaper is
 * published as a CSS custom property, with the same three selectors the
 * generated token block uses, and the tree draws `var(--wt-wallpaper-canvas)`:
 * a value the token lint is content with and the appearance can reach.
 *
 * It publishes EVERY wallpaper and not only the chosen one, because the picker
 * needs a swatch each and a swatch is the same problem in miniature — seven
 * names carry nothing, and a swatch painted from the tree would show the light
 * colour to somebody sitting in dark, which is the one thing a preview must
 * not do.
 */
export function wallpaperCss(chosen: ChatWallpaper): string {
  const light: string[] = [`  --wt-wallpaper-canvas: ${cssColor(chosen.canvasLight)};`];
  const dark: string[] = [`  --wt-wallpaper-canvas: ${cssColor(chosen.canvasDark)};`];
  for (const w of WALLPAPERS) {
    light.push(`  --wt-wallpaper-${w.id}: ${cssColor(w.canvasLight)};`);
    dark.push(`  --wt-wallpaper-${w.id}: ${cssColor(w.canvasDark)};`);
  }
  return [
    `:root {`,
    ...light,
    `}`,
    `@media (prefers-color-scheme: dark) {`,
    `  :root:not([data-appearance="light"]) {`,
    ...dark.map((l) => `  ${l}`),
    `  }`,
    `}`,
    `:root[data-appearance="dark"] {`,
    ...dark,
    `}`,
  ].join("\n");
}

/** What the conversation draws its canvas with. A `var(…)`, never a hex — see
 *  `wallpaperCss`. */
export const WALLPAPER_VAR = "var(--wt-wallpaper-canvas)";
